import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
  ModalDescription,
} from '@/components/_ui/modal';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { handleDomBubbleEvent } from '@/utils/dom';
import { memo, useCallback, useMemo } from 'react';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { AxiosErrorCustom, axiosExtractErrorMessage } from '@/lib/axios';
import { useNotificationActions } from '@/features/notifications/stores/notification.store';
import { useSelectedCases, useSelectedMenuCaseMode, useCasesActions } from '../../../stores/cases.store';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { useResolveCases } from '../../../api/resolve-cases.api';
import useSelectedCaseQs from '../../../hooks/use-case-qs';

export type TResolveCaseModalType = 'ResolveCaseModal';
export type IResolveCaseModalProps = {
  caseId?: string | null; // Optional: If provided, only resolve this conversation
};

const ResolveCaseModal = memo(({ caseId }: IResolveCaseModalProps) => {
  // HOOKS
  const { handleSelectedCase } = useSelectedCaseQs();
  const showToast = useCustomToast();
  const selectedCases = useSelectedCases();
  const selectedCaseMode = useSelectedMenuCaseMode();
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const { setResetSelectedCases, setResetSelectedMenuCaseMode, setResetSelectedCase } = useCasesActions();

  const isOpenResolveCaseModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('ResolveCaseModal');
  }, [caseModuleModalRootActions]);

  const caseIds = useMemo(() => {
    return selectedCases?.map((item) => item?.caseId) || [];
  }, [selectedCases]);

  const mutationResolveCase = useResolveCases({
    mutationConfig: {
      onSuccess: () => {
        showToast({
          type: 'success',
          message: 'Case resolved successfully!',
        });
        caseModuleModalRootActions.closeModal('ResolveCaseModal');
        handleSelectedCase(null);
        setResetSelectedCases();
        setResetSelectedCase();
        if (selectedCaseMode !== 'idle') {
          setResetSelectedMenuCaseMode();
        }
      },
      onError: (error: Error) => {
        showToast({
          type: 'error',
          message: axiosExtractErrorMessage(error as AxiosErrorCustom) || 'Something went wrong when resolve case!',
        });
        caseModuleModalRootActions.closeModal('ResolveCaseModal');
      },
    },
  });

  // EVENT HANDLERS
  const handleCloseModal = useCallback(() => {
    caseModuleModalRootActions.closeModal('ResolveCaseModal');
    if (selectedCaseMode === 'idle') {
      setResetSelectedCases();
    }
  }, [caseModuleModalRootActions, selectedCaseMode, setResetSelectedCases]);

  const handleSubmit = useCallback(() => {
    const caseIdsFromStore = selectedCases?.map((item) => item?.caseId) || [];
    const caseIds = caseId ? [caseId] : caseIdsFromStore;

    if (caseIds?.length === 0) {
      showToast({
        type: 'error',
        message: 'Please select at least one conversation',
      });
      return;
    }
    mutationResolveCase.mutate({
      caseIds,
    });
  }, [caseId, selectedCases, showToast, mutationResolveCase, caseIds]);

  const displayFormDesc = useMemo(() => {
    if (selectedCases?.length === 1) {
      return <span className="text-[14px] font-normal text-[#9D9D9C]">Would you like to close this case ?</span>;
    }
    return (
      <span className="text-[14px] font-normal text-[#9D9D9C]">
        Are you sure you want to close{' '}
        <span className="font-bold text-[#9D9D9C] px-[2px]">{selectedCases?.length} cases? </span>
        <br /> Once closed, you cannot reply within this chat room.
      </span>
    );
  }, [selectedCases?.length]);

  // EARLY RETURN
  if (!isOpenResolveCaseModal) {
    return null;
  }

  // RENDER LOGIC
  return (
    <Modal
      isCloseOutside={false}
      isButtonClose={true}
      onClose={handleCloseModal}
      className="w-[500px] p-[24px]"
      isOpen={isOpenResolveCaseModal}
    >
      <ModalHeader>
        <ModalTitle>Close case?</ModalTitle>
        <ModalDescription>{displayFormDesc}</ModalDescription>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            handleSubmit();
          }}
          id="resolve-case-form"
          className="grid gap-[16px]"
        ></form>
      </ModalContent>
      <ModalFooter className="gap-[12px]">
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="resolve-case-form" type="submit" disabled={mutationResolveCase.isPending}>
          {mutationResolveCase.isPending ? <LoadingSpinner /> : null}
          <span>Resolve</span>
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

ResolveCaseModal.displayName = 'ResolveCaseModal';

export default ResolveCaseModal;
